import { useCallback, useEffect, useState } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface FeeRule {
  id: string;
  legalProductId: string;
  legalProductName: string | null;
  name: string;
  description: string | null;
  feeType: string;
  baseFee: number | null;
  entryFee: number | null;
  successFeePercentage: number | null;
  minFee: number | null;
  maxFee: number | null;
  installmentCount: number | null;
  installmentValue: number | null;
  dueDay: number | null;
  isDefault: boolean;
  isActive: boolean;
  paymentNotes: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeeRuleInput {
  legalProductId: string;
  name: string;
  description?: string | null;
  feeType?: string;
  baseFee?: number | null;
  entryFee?: number | null;
  successFeePercentage?: number | null;
  minFee?: number | null;
  maxFee?: number | null;
  installmentCount?: number | null;
  installmentValue?: number | null;
  dueDay?: number | null;
  isDefault?: boolean;
  isActive?: boolean;
  paymentNotes?: string | null;
  notes?: string | null;
}

export type UpdateFeeRuleInput = CreateFeeRuleInput;

type UseFeeRulesOptions = {
  autoLoad?: boolean;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const toNullableInteger = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isInteger(number) ? number : null;
};

const toBoolean = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();

    if (['0', 'false', 'inactive', 'inativo', 'nao', 'não'].includes(normalized)) {
      return false;
    }

    if (['1', 'true', 'active', 'ativo', 'sim', 's'].includes(normalized)) {
      return true;
    }
  }

  return Boolean(value);
};

const sortFeeRules = (a: FeeRule, b: FeeRule) => {
  const productComparison = (a.legalProductName ?? a.name).localeCompare(
    b.legalProductName ?? b.name,
    'pt-BR'
  );

  if (productComparison !== 0) {
    return productComparison;
  }

  if (a.isDefault !== b.isDefault) {
    return a.isDefault ? -1 : 1;
  }

  return a.name.localeCompare(b.name, 'pt-BR');
};

const normalizeFeeRule = (value: any): FeeRule => ({
  id: String(value?.id ?? ''),
  legalProductId: String(value?.legalProductId ?? value?.legal_product_id ?? '').trim(),
  legalProductName: toNullableText(value?.legalProductName ?? value?.legal_product_name),
  name: String(value?.name ?? '').trim(),
  description: toNullableText(value?.description),
  feeType: String(value?.feeType ?? value?.fee_type ?? 'fixed').trim() || 'fixed',
  baseFee: toNullableNumber(value?.baseFee ?? value?.base_fee),
  entryFee: toNullableNumber(value?.entryFee ?? value?.entry_fee),
  successFeePercentage: toNullableNumber(value?.successFeePercentage ?? value?.success_fee_percentage),
  minFee: toNullableNumber(value?.minFee ?? value?.min_fee),
  maxFee: toNullableNumber(value?.maxFee ?? value?.max_fee),
  installmentCount: toNullableInteger(value?.installmentCount ?? value?.installment_count),
  installmentValue: toNullableNumber(value?.installmentValue ?? value?.installment_value),
  dueDay: toNullableInteger(value?.dueDay ?? value?.due_day),
  isDefault: toBoolean(value?.isDefault ?? value?.is_default),
  isActive: toBoolean(value?.isActive ?? value?.is_active),
  paymentNotes: toNullableText(value?.paymentNotes ?? value?.payment_notes),
  notes: toNullableText(value?.notes),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

export function useFeeRules(options: UseFeeRulesOptions = {}) {
  const { autoLoad = true } = options;
  const [feeRules, setFeeRules] = useState<FeeRule[]>([]);
  const [isLoadingFeeRules, setIsLoadingFeeRules] = useState(true);
  const [feeRulesError, setFeeRulesError] = useState<string | null>(null);

  const loadFeeRules = useCallback(async (legalProductId?: string) => {
    setIsLoadingFeeRules(true);
    setFeeRulesError(null);

    try {
      const endpoint = legalProductId
        ? `/api/fee-rules?legalProductId=${encodeURIComponent(legalProductId)}`
        : '/api/fee-rules';

      const response = await fetchWithRetry(endpoint);
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.feeRules)) {
        throw new Error(data.error || `GET /api/fee-rules falhou com status ${response.status}`);
      }

      setFeeRules(data.feeRules.map(normalizeFeeRule).sort(sortFeeRules));
    } catch (error) {
      console.error('Erro ao carregar regras de honorários reais. ', error);
      setFeeRules([]);
      setFeeRulesError('Não foi possível carregar os dados reais. Tente novamente.');
    } finally {
      setIsLoadingFeeRules(false);
    }
  }, []);

  const createFeeRule = useCallback(async (input: CreateFeeRuleInput) => {
    const response = await fetch('/api/fee-rules', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.feeRule) {
      throw new Error(data.error || `POST /api/fee-rules falhou com status ${response.status}`);
    }

    const created = normalizeFeeRule(data.feeRule);
    setFeeRules((prev) => [created, ...prev.filter((rule) => rule.id !== created.id)].sort(sortFeeRules));
    return created;
  }, []);

  const updateFeeRule = useCallback(async (id: string, input: UpdateFeeRuleInput) => {
    const response = await fetch(`/api/fee-rules/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.feeRule) {
      throw new Error(data.error || `PUT /api/fee-rules/${id} falhou com status ${response.status}`);
    }

    const updated = normalizeFeeRule(data.feeRule);
    setFeeRules((prev) => [updated, ...prev.filter((rule) => rule.id !== updated.id)].sort(sortFeeRules));
    return updated;
  }, []);

  const deleteFeeRule = useCallback(async (id: string) => {
    const response = await fetch(`/api/fee-rules/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `DELETE /api/fee-rules/${id} falhou com status ${response.status}`);
    }

    setFeeRules((prev) => prev.filter((rule) => rule.id !== id));
    return true;
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadFeeRules();
  }, [autoLoad, loadFeeRules]);

  return {
    feeRules,
    isLoadingFeeRules,
    feeRulesError,
    loadFeeRules,
    createFeeRule,
    updateFeeRule,
    deleteFeeRule,
  };
}
